import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface PersistedConfigContextValue<T> {
  config: T;
  /** Shallow-merge a partial update into the config */
  update: (patch: Partial<T>) => void;
  /** Deep-set a value at a dot-delimited path */
  set: (path: string, value: unknown) => void;
  /** Reset everything to defaults */
  reset: () => void;
}

/** Recursively fills missing keys from `defaults`; arrays and leaves come from `overrides`. */
export function mergeWithDefaults(
  defaults: unknown,
  overrides: unknown
): unknown {
  if (
    overrides === null ||
    overrides === undefined ||
    typeof overrides !== 'object' ||
    Array.isArray(overrides)
  ) {
    return overrides ?? defaults;
  }
  if (
    typeof defaults !== 'object' ||
    defaults === null ||
    Array.isArray(defaults)
  ) {
    return overrides;
  }
  const result: Record<string, unknown> = {
    ...(defaults as Record<string, unknown>),
  };
  for (const key of Object.keys(overrides as Record<string, unknown>)) {
    result[key] = mergeWithDefaults(
      (defaults as Record<string, unknown>)[key],
      (overrides as Record<string, unknown>)[key]
    );
  }
  return result;
}

function setAtPath<T>(source: T, path: string, value: unknown): T {
  const next = structuredClone(source);
  const keys = path.split('.');
  let cursor = next as unknown as Record<string, unknown>;
  for (let index = 0; index < keys.length - 1; index++) {
    const key = keys[index];
    const child = cursor[key];
    if (!child || typeof child !== 'object' || Array.isArray(child)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
}

export interface PersistedConfigStore<T> {
  storageKey: string;
  defaults: T;
  /** Parses a stored JSON string and fills in missing defaults; throws on bad JSON. */
  parse: (raw: string) => T;
  load: () => T;
  save: (config: T) => void;
}

export function createPersistedConfigStore<T extends object>(
  storageKey: string,
  defaults: T
): PersistedConfigStore<T> {
  const parse = (raw: string): T =>
    mergeWithDefaults(defaults, JSON.parse(raw)) as T;
  const load = (): T => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? parse(raw) : defaults;
    } catch {
      return defaults;
    }
  };
  const save = (config: T): void => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(config));
    } catch {
      // Storage can be blocked (private mode, quota); keep in-memory config.
    }
  };
  return { storageKey, defaults, parse, load, save };
}

/** Context + provider that auto-persists to localStorage and syncs across tabs. */
export function createPersistedConfigContext<T extends object>(
  store: PersistedConfigStore<T>
) {
  const noop = () => undefined;
  const Ctx = createContext<PersistedConfigContextValue<T>>({
    config: store.defaults,
    update: noop,
    set: noop,
    reset: noop,
  });

  function Provider({ children }: { children: ReactNode }) {
    const [config, setConfig] = useState<T>(store.load);

    useEffect(() => {
      store.save(config);
    }, [config]);

    useEffect(() => {
      const onStorage = (event: StorageEvent) => {
        if (event.key !== store.storageKey || !event.newValue) return;
        try {
          setConfig(store.parse(event.newValue));
        } catch {
          // Ignore malformed writes from other tabs.
        }
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }, []);

    const update = useCallback((patch: Partial<T>) => {
      setConfig((previous) => ({ ...previous, ...patch }));
    }, []);
    const set = useCallback((path: string, value: unknown) => {
      setConfig((previous) => setAtPath(previous, path, value));
    }, []);
    const reset = useCallback(() => setConfig(store.defaults), []);

    const value = useMemo(
      () => ({ config, update, set, reset }),
      [config, update, set, reset]
    );
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  const useConfig = (): PersistedConfigContextValue<T> => useContext(Ctx);
  return { Provider, useConfig };
}
